import React, {
  useCallback, useState, useEffect, useMemo,
} from 'react';

import {
  Flex, Text, useDisclosure, useToast,
} from '@chakra-ui/react';
import { isNaN } from 'lodash';
import { useQuery } from 'react-query';
import { useParams, useLocation, useHistory } from 'react-router-dom';
import { useStateList } from 'react-use';

import api from 'api';
import { Button, TableAction, Icon } from 'components';
import { breadcrumbs, translatedNames } from 'constants/routes';
import {
  newButtonText, actions, baseFilters, breadcrumbParent,
} from 'constants/table';
import BreadcrumbMenu from 'containers/Breadcrumb';
import Page from 'containers/Page';
import Table from 'containers/Table';
import ExportUsers from 'containers/Table/ExportUsers';
import Filters from 'containers/Table/Filter';
import GenerateReport from 'containers/Table/GenerateReport';
import ImportFile from 'containers/Table/ImportFile';
import { ContentRoutes } from 'enums/routes';
import { AccessTypes, MethodTypes } from 'enums/types';
import { policy } from 'polices';
import useTableStore from 'stores/useTable';
import useUser from 'stores/useUser';
import { handleObjectString } from 'utils/parsers';

import { useFormModal } from '../hooks/useFormModal';

export default function TablePage() {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { content, id } = useParams<GeneralRoute>();
  const tableStore = useTableStore();
  const location = useLocation();
  const { push } = useHistory();
  const { user } = useUser();
  const toast = useToast();

  const { state: orderType, next: toggle } = useStateList(['desc', 'asc']);
  const [checkboxFilters, setCheckboxFilters] = useState({});
  const [filters, setFilters] = useState({});
  const [order, setOrder] = useState('id');
  const [page, setPage] = useState(1);
  const fallbackContent = useMemo(() => (id && isNaN(Number(id)) ? content.concat(`/${id}`) : content), [content, id]);
  const policies = policy(fallbackContent);
  const table = useTableStore(useCallback(
    (state) => state.tables[fallbackContent], [fallbackContent, content]));

  const { Form, onOpen: onOpenModal } = useFormModal(content);
  useEffect(() => {
    if (policies(MethodTypes.View) === AccessTypes.Disallow) {
      push('/403');
    }
  }, [fallbackContent]);

  useEffect(() => {
    setOrder('id');
    setPage(1);
    setFilters({});
    setCheckboxFilters({});

    const searchParams = {};
    new URLSearchParams(location.search)
      .forEach((value, key) => { searchParams[key] = value; });
    setFilters({ ...(location.state as object), ...searchParams });
  }, [location]);

  const refresh = useCallback(() => {
    if (page === 1) {
      refetch();
    } else {
      setPage(1);
    }
  }, [page]);

  useEffect(() => {
    refresh();
  }, [order, orderType, filters, checkboxFilters]);

  const { refetch, isFetching } = useQuery(
    [page, order, orderType, filters, checkboxFilters],
    async () => api().get(content.concat(id ? `/${id}` : ''), {
      params: {
        orderType,
        perPage: table?.meta?.perPage || 10,
        page,
        orderBy: order.split('.')[0],
        ...filters,
        ...checkboxFilters,
      },
    }),
    {
      cacheTime: 0,
      refetchOnWindowFocus: false,
      queryKey: [page, filters],
      onSuccess: (data) => {
        if (page > 1) {
          tableStore.setTable(fallbackContent, {
            ...data.data,
            data: [...table?.data || [], ...data.data.data],
          });
        } else {
          tableStore.setTable(fallbackContent, {
            meta: data.data.meta,
            data: data.data.data,
          });
        }
      },
      onError: (error: any) => {
        toast({
          title: 'Ocorreu um erro.',
          description: error.message,
          status: 'error',
          duration: 5000,
          position: 'top-right',
          isClosable: true,
        });
      },
    },
  );

  const onPaginate = useCallback(() => {
    setPage((prev) => prev + 1);
  }, []);

  const createActions = useMemo(() => ({
    modal: () => onOpenModal(),
    page: () => push(`/${fallbackContent}`),
  }), [fallbackContent]);

  const ImportAction = useMemo(() => ({
    file: () => (
      <ImportFile
        onSuccess={() => refresh()}
        content={fallbackContent}
      />
    ),
    null: () => null,
  }[actions[fallbackContent]?.onImport]), [fallbackContent]);

  const parentTable = useMemo(() => {
    if (table?.data?.[0] && breadcrumbParent[fallbackContent]) {
      return {
        path: location.pathname,
        label: handleObjectString(table.data[0], breadcrumbParent[fallbackContent]),
      };
    }
    return null;
  }, [table, fallbackContent]);

  return (
    <Page>
      <Form onSuccess={refresh} />
      <Flex
        mx="auto"
        w="100%"
        p={5}
        flexDirection="column">
        <BreadcrumbMenu paths={[
          ...breadcrumbs[fallbackContent],
          ...(parentTable ? [parentTable] : []),
        ]}
        />
        <Flex
          py={4}
          px={2}
          flexDir="row"
          justifyContent="space-between">
          <Text variant="4xLargeBold">{parentTable ? parentTable.label : translatedNames[fallbackContent]}</Text>
          <Flex>
            {baseFilters[fallbackContent] && (
            <>
              <TableAction
                text="Filtrar"
                mr={5}
                h={9}
                onClick={onOpen}
                _hover={{ bg: 'gray.200' }}
                icon="sort-amount-down-alt"
              />
              <Filters
                onClose={onClose}
                isOpen={isOpen}
                filters={checkboxFilters}
                setFilters={setCheckboxFilters}
              />
            </>
            )}
            {policies(MethodTypes.Create) !== AccessTypes.Disallow
             && (
             <>
               {content === ContentRoutes.Users && <ExportUsers />}
               {content === ContentRoutes.Routes && <GenerateReport />}
               {!user?.isSuperUser && <ImportAction />}
               <Button
                 hidden={!actions[fallbackContent]?.onCreate}
                 onClick={createActions[actions[fallbackContent]?.onCreate]}
                 size="xs"
                 h={9}
                 variant="primary.blue">
                 <Icon
                   mr={2}
                   name="plus"
                   family="fas"
                 />
                 <Text
                   style={{ lineHeight: 0 }}
                   variant="smallBold">
                   {newButtonText[fallbackContent]}
                 </Text>
               </Button>
             </>
             )}
          </Flex>
        </Flex>
        {!!table && (
        <Table
          setOrderby={setOrder}
          toggleOrderType={toggle}
          order={order}
          orderType={orderType}
          onPaginate={onPaginate}
          page={page}
          isFetching={isFetching}
          refetch={refresh}
          fallbackContent={fallbackContent}
        />
        )}
      </Flex>
    </Page>
  );
}
